import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { OPENING_STATUS } from 'src/app/app.constants';
import { config } from 'src/app/environment-config';
import { initFlags } from 'src/app/featureFlags';
import { AppState } from 'src/app/state/app-state';
import {
  BOOTSTRAP_SESSION,
  CENTRAL_HOUR_INITIALIZER,
  CONTRACTS_INITIALIZER,
  FEATURE_FLAGS,
  INIT_ROLLOUT,
  IVA_INITIALIZER,
  MARKET_OPERATION_TIME_INITIALIZER,
  OPENING,
} from 'src/app/state/app.reducer';
import { ContractsService } from 'src/core/services/contracts.service';
import { MarketService } from 'src/core/services/market.service';
import { MixpanelService } from 'src/core/services/mixpanel.service';
import { UtilitiesService } from 'src/core/services/utilities.service';
import { SolaceProxyService } from 'src/core/solace/solace.proxy.service';
import { AuthService } from './auth-service.service';
import { SessionService } from './session.service';
import { NewRelicLogger } from 'src/components/shared/newrelicLogger/newRelicLogger.class';

const { DOTCOM, FEATURE_MANAGER_API_KEY } = config();

@Injectable()
export class AppService {
  private initializersSubscription: Subscription;

  constructor(
    private store: Store<AppState>,
    private utilitiesService: UtilitiesService,
    private marketService: MarketService,
    private contractsService: ContractsService,
    private solaceProxyService: SolaceProxyService,
    private sessionService: SessionService,
    private mixpanelService: MixpanelService,
    private authService: AuthService,
    private newRelic: NewRelicLogger,
  ) {}

  _init(): void {
    this.startFlagsSetup();
    this.validateContractOpening();
    this.initElastic();

    this.utilitiesService
      .getCentralHour()
      .pipe(take(1))
      .subscribe(() => {
        this.store.dispatch({
          type: CENTRAL_HOUR_INITIALIZER,
          payload: true,
        });
      });

    this.store.dispatch({
      type: IVA_INITIALIZER,
      payload: true,
    });

    this.marketService
      .getCapitalMarketOperationTime()
      .pipe(take(1))
      .subscribe(
        () => {
          this.store.dispatch({
            type: MARKET_OPERATION_TIME_INITIALIZER,
            payload: true,
          });
        },
        () => {
          // Do nothing, just catch error
        },
      );

    this.contractsService.getAccountManagerElegibility();

    this.contractsService.getContractsBP().subscribe(
      () => {
        this.store.dispatch({
          type: CONTRACTS_INITIALIZER,
          payload: true,
        });

        this.solaceProxyService.init();
      },
      () => {
        // Do nothing, just catch error
      },
    );

    this.initializersSubscription = this.store
      .select('initializers')
      .pipe(map((values: any) => values.toJS()))
      .subscribe((values: any) => {
        if (
          values.centralHour &&
          values.IVA &&
          values.contracts &&
          values.solace &&
          values.marketOperationTime
        ) {
          this.store.dispatch({
            type: BOOTSTRAP_SESSION,
            payload: true,
          });

          this.initializersSubscription.unsubscribe();
          this.startMixpanelSetup();
        }
      });
  }

  init(): void {
    const WAIT_TIME = 80;

    if (this.authService.getAuthorizationSession() !== undefined) {
      this._init();
    } else {
      setTimeout(() => {
        this.init();
      }, WAIT_TIME);
    }
  }

  private validateContractOpening() {
    this.contractsService.getContractOpening().subscribe(
      (contractOpeningData: any) => {
        const { is_legacy_user, opening_status, opening_type } =
          contractOpeningData;
        const canOperate =
          is_legacy_user ||
          opening_status === OPENING_STATUS.ACTIVE ||
          contractOpeningData.has_any_account_with_position_different_than_zero;

        !canOperate && window.location.assign(DOTCOM.BASE_URL);

        this.store.dispatch({
          type: OPENING,
          payload: {
            openingType: opening_type,
          },
        });
      },
      () => {
        // Do nothing, just catch error
      },
    );
  }

  private startFlagsSetup() {
    try {
      const userId = this.sessionService.getUserId();
      const userEmail = this.sessionService.getEmail(
        this.authService.getAuthorizationSession(),
      );

      initFlags(
        {
          apiKey: FEATURE_MANAGER_API_KEY,
          userEmail,
          userId,
        },
        (flags) => {
          this.store.dispatch({ type: INIT_ROLLOUT, payload: true });
          this.store.dispatch({
            type: FEATURE_FLAGS,
            payload: flags,
          });
        },
      );
    } catch (err) {
      console.warn(err);
    }
  }

  private initElastic() {
    const userId = this.sessionService.getUserId();
    const userEmail = this.sessionService.getEmail(
      this.authService.getAuthorizationSession(),
    );
    this.newRelic.init(userId, userEmail);
  }

  private startMixpanelSetup() {
    const userId = this.sessionService.getUserId();
    const userEmail = this.sessionService.getEmail(
      this.authService.getAuthorizationSession(),
    );
    this.mixpanelService.init(userId, userEmail);
  }
}
